import React, { useCallback, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalculatorIcon, ChevronDownIcon, SaveIcon, TableIcon, ArrowRightLeftIcon } from 'lucide-react';
import { calculateLeasingRate, calculateNAWFromRate, calculateAllTerms, formatCurrency, formatNumber, TERMS, TermMonths, NAW_BANDS, LEASING_FACTORS } from '../utils/leasingFactors';
interface LeasingCalculatorProps {
  onSaveCalculation: (naw: number, term: TermMonths, monthlyRate: number, factor: number) => void;
}
export function LeasingCalculator({
  onSaveCalculation
}: LeasingCalculatorProps) {
  const [nawInput, setNawInput] = useState<string>('');
  const [rateInput, setRateInput] = useState<string>('');
  const [selectedTerm, setSelectedTerm] = useState<TermMonths>(36);
  const [showExpertTable, setShowExpertTable] = useState(false);
  const [mode, setMode] = useState<'naw-to-rate' | 'rate-to-naw'>('naw-to-rate');
  // Parse NAW input (handle German number format)
  const nawValue = useMemo(() => {
    if (!nawInput) return 0;
    const normalized = nawInput.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  }, [nawInput]);
  // Parse Rate input (handle German number format)
  const rateValue = useMemo(() => {
    if (!rateInput) return 0;
    const normalized = rateInput.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  }, [rateInput]);
  // Calculate result instantly (forward: NAW → Rate)
  const result = useMemo(() => {
    if (mode !== 'naw-to-rate' || nawValue === 0) return null;
    return calculateLeasingRate(nawValue, selectedTerm);
  }, [nawValue, selectedTerm, mode]);
  // Calculate reverse result (Rate → NAW)
  const reverseResult = useMemo(() => {
    if (mode !== 'rate-to-naw' || rateValue === 0) return null;
    return calculateNAWFromRate(rateValue, selectedTerm);
  }, [rateValue, selectedTerm, mode]);
  // Calculate all terms for expert view
  const allTermsResults = useMemo(() => {
    if (mode === 'naw-to-rate') {
      if (nawValue === 0) return null;
      return calculateAllTerms(nawValue);
    }
    return null;
  }, [nawValue, mode]);
  const handleNawChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[\d.,]*$/.test(value)) {
      setNawInput(value);
    }
  }, []);
  const handleRateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[\d.,]*$/.test(value)) {
      setRateInput(value);
    }
  }, []);
  const handleSave = useCallback(() => {
    if (mode === 'naw-to-rate' && result?.isValid) {
      onSaveCalculation(nawValue, selectedTerm, result.monthlyRate, result.factor);
    } else if (mode === 'rate-to-naw' && reverseResult?.isValid) {
      onSaveCalculation(reverseResult.naw, selectedTerm, rateValue, reverseResult.factor);
    }
  }, [result, reverseResult, nawValue, rateValue, selectedTerm, onSaveCalculation, mode]);
  const toggleMode = useCallback(() => {
    setMode(prev => prev === 'naw-to-rate' ? 'rate-to-naw' : 'naw-to-rate');
  }, []);
  // Determine which result to show for expert table highlighting
  const activeResult = mode === 'naw-to-rate' ? result : null;
  return <div className="space-y-6" data-id="element-50">
      {/* Input Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-stone-200" data-id="element-51">
        <div className="space-y-5" data-id="element-52">
          {/* Mode Toggle */}
          <div className="flex items-center justify-between" data-id="element-53">
            <span className="text-sm font-medium text-stone-600" data-id="element-54">
              {mode === 'naw-to-rate' ? 'NAW → Monatliche Rate' : 'Monatliche Rate → NAW'}
            </span>
            <button onClick={toggleMode} className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-stone-600 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors" aria-label="Berechnungsrichtung umschalten" data-id="element-55">
              <ArrowRightLeftIcon className="w-3.5 h-3.5" data-id="element-56" />
              Umkehren
            </button>
          </div>

          {/* Input Field */}
          <AnimatePresence mode="wait" data-id="element-57">
            {mode === 'naw-to-rate' ? <motion.div key="naw-input" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 10
          }} transition={{
            duration: 0.15
          }} data-id="element-58">
                <label htmlFor="naw-input" className="block text-sm font-medium text-stone-600 mb-2" data-id="element-59">
                  Netto-Anschaffungswert (NAW)
                </label>
                <div className="relative" data-id="element-60">
                  <input id="naw-input" type="text" inputMode="decimal" value={nawInput} onChange={handleNawChange} placeholder="z.B. 25.000" className="w-full px-4 py-3 pr-16 text-lg font-medium text-stone-900 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition-all" aria-describedby="naw-hint" data-id="element-61" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium" data-id="element-62">
                    EUR
                  </span>
                </div>
                <p id="naw-hint" className="mt-1.5 text-xs text-stone-500" data-id="element-63">
                  Ab 500,00 EUR
                </p>
              </motion.div> : <motion.div key="rate-input" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 10
          }} transition={{
            duration: 0.15
          }} data-id="element-64">
                <label htmlFor="rate-input" className="block text-sm font-medium text-stone-600 mb-2" data-id="element-65">
                  Gewünschte monatliche Rate
                </label>
                <div className="relative" data-id="element-66">
                  <input id="rate-input" type="text" inputMode="decimal" value={rateInput} onChange={handleRateChange} placeholder="z.B. 500" className="w-full px-4 py-3 pr-16 text-lg font-medium text-stone-900 bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-stone-400 focus:border-transparent transition-all" aria-describedby="rate-hint" data-id="element-67" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 font-medium" data-id="element-68">
                    EUR
                  </span>
                </div>
                <p id="rate-hint" className="mt-1.5 text-xs text-stone-500" data-id="element-69">
                  Monatliche Wunschrate eingeben
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Term Selection */}
          <div data-id="element-70">
            <label className="block text-sm font-medium text-stone-600 mb-2" data-id="element-71">
              Laufzeit (Monate)
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-72">
              {TERMS.map(term => <button key={term} onClick={() => setSelectedTerm(term)} className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${selectedTerm === term ? 'bg-stone-700 text-white shadow-md' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} aria-pressed={selectedTerm === term} data-id="element-73">
                  {term}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Result Display — Forward Mode */}
      <AnimatePresence mode="wait" data-id="element-74">
        {mode === 'naw-to-rate' && result && <motion.div key="forward-result" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} className={`rounded-2xl p-6 ${result.isValid ? 'bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-200' : 'bg-amber-50 border border-amber-200'}`} data-id="element-75">
            {result.isValid ? <div className="space-y-4" data-id="element-76">
                <div className="flex items-start justify-between" data-id="element-77">
                  <div data-id="element-78">
                    <p className="text-sm text-stone-600 font-medium mb-1" data-id="element-79">
                      Monatliche Leasingrate
                    </p>
                    <p className="text-4xl font-bold text-stone-900 tracking-tight" data-id="element-80">
                      {formatCurrency(result.monthlyRate)}
                    </p>
                  </div>
                  <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white rounded-xl font-medium text-sm hover:bg-stone-800 transition-colors shadow-sm" aria-label="Berechnung speichern" data-id="element-81">
                    <SaveIcon className="w-4 h-4" data-id="element-82" />
                    Speichern
                  </button>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-stone-600" data-id="element-83">
                  <span data-id="element-84">
                    NAW: <strong data-id="element-85">{formatCurrency(nawValue)}</strong>
                  </span>
                  <span data-id="element-86">
                    Laufzeit: <strong data-id="element-87">{selectedTerm} Monate</strong>
                  </span>
                  <span data-id="element-88">
                    Faktor: <strong data-id="element-89">{formatNumber(result.factor)}%</strong>
                  </span>
                </div>
              </div> : <div className="flex items-center gap-3" data-id="element-90">
                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center" data-id="element-91">
                  <CalculatorIcon className="w-5 h-5 text-amber-700" data-id="element-92" />
                </div>
                <p className="text-amber-800 font-medium" data-id="element-93">
                  {result.errorMessage}
                </p>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Result Display — Reverse Mode */}
      <AnimatePresence mode="wait" data-id="element-94">
        {mode === 'rate-to-naw' && reverseResult && <motion.div key="reverse-result" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} className={`rounded-2xl p-6 ${reverseResult.isValid ? 'bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-200' : 'bg-amber-50 border border-amber-200'}`} data-id="element-95">
            {reverseResult.isValid ? <div className="space-y-4" data-id="element-96">
                <div className="flex items-start justify-between" data-id="element-97">
                  <div data-id="element-98">
                    <p className="text-sm text-stone-600 font-medium mb-1" data-id="element-99">
                      Finanzierbarer Netto-Anschaffungswert
                    </p>
                    <p className="text-4xl font-bold text-stone-900 tracking-tight" data-id="element-100">
                      {formatCurrency(reverseResult.naw)}
                    </p>
                  </div>
                  <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-stone-700 text-white rounded-xl font-medium text-sm hover:bg-stone-800 transition-colors shadow-sm" aria-label="Berechnung speichern" data-id="element-101">
                    <SaveIcon className="w-4 h-4" data-id="element-102" />
                    Speichern
                  </button>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-stone-600" data-id="element-103">
                  <span data-id="element-104">
                    Mtl. Rate: <strong data-id="element-105">{formatCurrency(rateValue)}</strong>
                  </span>
                  <span data-id="element-106">
                    Laufzeit: <strong data-id="element-107">{selectedTerm} Monate</strong>
                  </span>
                  <span data-id="element-108">
                    Faktor:{' '}
                    <strong data-id="element-109">{formatNumber(reverseResult.factor)}%</strong>
                  </span>
                </div>
              </div> : <div className="flex items-center gap-3" data-id="element-110">
                <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center" data-id="element-111">
                  <CalculatorIcon className="w-5 h-5 text-amber-700" data-id="element-112" />
                </div>
                <p className="text-amber-800 font-medium" data-id="element-113">
                  {reverseResult.errorMessage}
                </p>
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Expert Toggle Button */}
      <button onClick={() => setShowExpertTable(!showExpertTable)} className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-stone-600 hover:text-stone-900 transition-colors" aria-expanded={showExpertTable} data-id="element-114">
        <TableIcon className="w-4 h-4" data-id="element-115" />
        Experte
        <ChevronDownIcon className={`w-4 h-4 transition-transform ${showExpertTable ? 'rotate-180' : ''}`} data-id="element-116" />
      </button>

      {/* Expert Table */}
      <AnimatePresence data-id="element-117">
        {showExpertTable && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-118">
            {/* All Terms for Current NAW */}
            {allTermsResults && <div className="bg-white rounded-2xl p-5 shadow-sm border border-stone-200 mb-4" data-id="element-119">
                <h3 className="text-sm font-semibold text-stone-700 mb-3" data-id="element-120">
                  Alle Laufzeiten für {formatCurrency(nawValue)}
                </h3>
                <div className="overflow-x-auto" data-id="element-121">
                  <table className="w-full text-sm" data-id="element-122">
                    <thead data-id="element-123">
                      <tr className="border-b border-stone-200" data-id="element-124">
                        <th className="text-left py-2 px-3 font-medium text-stone-500" data-id="element-125">
                          Laufzeit
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-stone-500" data-id="element-126">
                          Faktor
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-stone-500" data-id="element-127">
                          Monatl. Rate
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-stone-500" data-id="element-128">
                          Summe Raten
                        </th>
                        <th className="text-right py-2 px-3 font-medium text-stone-500" data-id="element-129">
                          Finanz.kosten
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-130">
                      {allTermsResults.map(item => {
                  const totalPayments = item.monthlyRate * item.term;
                  const financingCosts = totalPayments - nawValue;
                  return <tr key={item.term} className={`border-b border-stone-100 ${item.term === selectedTerm ? 'bg-stone-100' : ''}`} data-id="element-131">
                            <td className="py-2.5 px-3 font-medium text-stone-700" data-id="element-132">
                              {item.term} M.
                            </td>
                            <td className="py-2.5 px-3 text-right text-stone-600" data-id="element-133">
                              {formatNumber(item.factor)}%
                            </td>
                            <td className="py-2.5 px-3 text-right font-semibold text-stone-900" data-id="element-134">
                              {formatCurrency(item.monthlyRate)}
                            </td>
                            <td className="py-2.5 px-3 text-right text-stone-600" data-id="element-135">
                              {formatCurrency(totalPayments)}
                            </td>
                            <td className="py-2.5 px-3 text-right text-stone-600" data-id="element-136">
                              {formatCurrency(financingCosts)}
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                </div>
              </div>}

            {/* Full Factor Table */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-stone-200" data-id="element-137">
              <h3 className="text-sm font-semibold text-stone-700 mb-3" data-id="element-138">
                Vollständige Faktortabelle
              </h3>
              <div className="overflow-x-auto" data-id="element-139">
                <table className="w-full text-xs" data-id="element-140">
                  <thead data-id="element-141">
                    <tr className="border-b border-stone-200" data-id="element-142">
                      <th className="text-left py-2 px-2 font-medium text-stone-500" data-id="element-143">
                        Laufzeit
                      </th>
                      {NAW_BANDS.map((band, i) => <th key={i} className="text-center py-2 px-2 font-medium text-stone-500 whitespace-nowrap" data-id="element-144">
                          {band.label}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-145">
                    {TERMS.map(term => <tr key={term} className="border-b border-stone-100" data-id="element-146">
                        <td className="py-2 px-2 font-medium text-stone-700" data-id="element-147">
                          {term}
                        </td>
                        {LEASING_FACTORS[term].map((factor, i) => <td key={i} className={`py-2 px-2 text-center ${activeResult?.isValid && term === selectedTerm && i === activeResult.bandIndex ? 'bg-stone-200 font-semibold text-stone-800' : 'text-stone-600'}`} data-id="element-148">
                            {formatNumber(factor)}
                          </td>)}
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-stone-500" data-id="element-149">
                * Bei NAW ab 100.000,01 EUR kontaktieren Sie bitte Ihren
                persönlichen Ansprechpartner.
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}